import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';

import { ROLES_STATUS } from '../../enums';
import { AccountDTO, TripCreateRequest, TripDTO } from '../../interfaces/models';
import { LoadingService } from '../../providers/loading/loading.service';
import { TripService } from '../../providers/trip/trip.service';
import { UserService } from '../../providers/user/user.service';
import { tap, mergeMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { Router } from '@angular/router';
import { NgSelectCompatModule } from '../../compat/ng-select/ng-select.module';
import { DirectivesModule } from '../../directives.module';

interface DialogData {
  id?: number;
  orderIds?: number[];
  dispatcherId?: number;
  driverId?: number;
  name?: string;
}

@Component({
  selector: 'app-create-trip-and-send',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    NgSelectCompatModule,
    DirectivesModule,
  ],
  templateUrl: './create-trip-and-send.component.html',
  styleUrls: ['./create-trip-and-send.component.scss'],
})
export class CreateTripAndSendComponent implements OnInit {
  drivers: AccountDTO[] = [];
  dispatchers: AccountDTO[] = [];
  limit = 10;
  skipDriver = 0;
  countDriver = 0;
  skipDispatcher = 0;
  countDispatcher = 0;
  currentUser: AccountDTO;
  roles = ROLES_STATUS;

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private tripService: TripService,
    private loadingService: LoadingService,
    public dialogRef: MatDialogRef<CreateTripAndSendComponent>,
    private readonly router: Router,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
  ) { }
  public dataForm = this.buildDataForm();

  ngOnInit() {
    this.currentUser = this.userService.user;
    this.dispatchers = [this.currentUser];
    this.getDrivers();
    if (this.currentUser.roleId !== ROLES_STATUS.DISPATCHER) {
      this.getDispatchers();
    } else {
      this.dataForm.controls.dispatcherId.setValue(this.currentUser.id);
    }
    const { dispatcherId, driverId, id } = this.data;
    if (id) {
      if (dispatcherId) {
        this.dataForm.controls.dispatcherId.setValue(this.data.dispatcherId);
      }
      if (driverId) {
        this.dataForm.controls.driverId.setValue(this.data.driverId);
      }
      this.dataForm.controls.dispatcherId.setValidators([Validators.required]);
    }
  }

  private getValue(key: string) {
    const value = this.dataForm.get(key).value;
    return Number(value);
  }

  public createTrip(isShouldSendToDriver: boolean) {
    const { orderIds } = this.data;
    if (orderIds && orderIds.length) {
      this.loadingService.startLoading();
      const body: TripCreateRequest = {
        orderIds: this.data.orderIds,
        dispatcherId: this.getValue('dispatcherId'),
        driverId: this.getValue('driverId') ? this.getValue('driverId') : null,
      };
      this.tripService.create(body)
        .pipe(
          mergeMap((trip: TripDTO) => {
            if (trip && trip.driverId && isShouldSendToDriver) {
              const action = trip.status === 'draft' ? 'publish' : 'move_to_draft';
              return this.tripService.changeState(trip.id, action);
            }

            return of(trip);
          }),
          tap((trip: TripDTO) => {
            this.dialogRef.close(trip);
            this.loadingService.stopLoading();
          },
            () => this.dialogRef.close()),
        )
        .subscribe();
    }
  }

  private getDrivers() {
    this.userService
      .getList(this.skipDriver, this.limit, null, null, ROLES_STATUS.DRIVER)
      .subscribe((res) => {
        const drivers = res.data;
        this.drivers = [...this.drivers, ...drivers];
        this.countDriver = res.count;
      });
  }

  public getListDrivers() {
    if (this.drivers.length < this.countDriver) {
      this.skipDriver = this.skipDriver + this.limit;
      this.getDrivers();
    }
  }

  private getDispatchers() {
    this.userService
      .getList(
        this.skipDispatcher,
        this.limit,
        null,
        null,
        ROLES_STATUS.DISPATCHER,
      )
      .subscribe((res) => {
        const dispatchers = res.data;
        this.dispatchers = [...this.dispatchers, ...dispatchers];
        this.countDriver = res.count;
      });
  }

  public getListDispatchers() {
    if (this.dispatchers.length < this.countDispatcher) {
      this.skipDispatcher = this.skipDispatcher + this.limit;
      this.getDispatchers();
    }
  }

  public closeDialog() {
    this.dialogRef.close();
  }

  public inviteDriver() {
    this.router.navigate(['accounts/list']);
    this.closeDialog();
  }

  public isDriverUnavailable(driver: AccountDTO) {
    return this.userService.isDriverUnavailable(driver);
  }

  private buildDataForm() {
    return this.fb.group({
      name: new FormControl(''),
      dispatcherId: new FormControl<number | null>(null),
      driverId: new FormControl<number | null>(null),
    });
  }
}
